// /volunteer — UMaps Volunteer Tools.
//
//   /volunteer            sign in, then pick one of the five tools
//   /volunteer/moderate   Content Moderator (events, restaurant reviews, photos)
//   /volunteer/issues     Issues & Reports Manager
//   /volunteer/content    Content Filler
//   /volunteer/rooms      Room Names
//   /volunteer/routes     Route tester (full-bleed map; the page gets `vt-bleed`)
//
// The six URLs are FIXED: /admin links straight to them. One page component
// for all six (index.js routes every /volunteer* path here) so the session,
// the header and the sign-in live in one place and moving between tools does
// not reload anything.
//
// ## Signing in is an emailed link, the Event Map Creator's flow
//
// Type your email → a one-use link arrives → opening it shows WHO it signs in
// and a button → pressing the button spends it. There is no password and no
// sign-up: only an address the operator granted (api/volunteers.js) ever gets
// an email, and the form says the same thing to everybody so it cannot be used
// to find out who volunteers here. The button exists because mail scanners open
// every link in an email; a page that signed you in on load would have its link
// spent by the university's spam filter. See api/volunteer-auth.js.
//
// ## Scope
//
// A volunteer may hold any of the five tools. The picker shows all five and
// greys out the ones this login does not include, with a line saying so —
// hiding them would leave a volunteer told "use the Content Filler" unable to
// tell whether they are looking in the wrong place or were never given it.
// The server enforces the same split on every request.

import { useCallback, useEffect, useMemo, useState } from 'react';
import UMapsWordmark from './components/UMapsWordmark.jsx';
import { CSS, TOOLS, api, clearSession, readToken, writeToken } from './volunteer/shared.js';
import Moderator from './volunteer/Moderator.jsx';
import Issues from './volunteer/Issues.jsx';
import ContentFiller from './volunteer/ContentFiller.jsx';
import RoomNames from './volunteer/RoomNames.jsx';
import RouteTester from './volunteer/RouteTester.jsx';

const pathNow = () => window.location.pathname.replace(/\/$/, '') || '/volunteer';

export default function VolunteerPage() {
  const [path, setPath] = useState(pathNow);
  const [token, setToken] = useState(readToken);
  const [me, setMe] = useState(null);          // { email, scopes, admin, name }
  const [booting, setBooting] = useState(true);
  const [signinLink] = useState(() => {
    try { return new URLSearchParams(window.location.search).get('signin') || ''; } catch { return ''; }
  });
  const [expiredMsg, setExpiredMsg] = useState('');

  useEffect(() => {
    const onPop = () => setPath(pathNow());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const go = useCallback((to) => {
    if (to === pathNow()) return;
    window.history.pushState({}, '', to);
    setPath(to);
    window.scrollTo(0, 0);
  }, []);

  const signOut = useCallback((why = '', all = false) => {
    clearSession(all);
    setToken('');
    setMe(null);
    setExpiredMsg(typeof why === 'string' ? why : '');
  }, []);

  // Who am I — on every load, so the tools shown are the ones the account has
  // NOW rather than the ones the token was minted with.
  useEffect(() => {
    if (!token) { setBooting(false); return; }
    let live = true;
    (async () => {
      const r = await api('/api/volunteer-auth', { token });
      if (!live) return;
      if (r.ok) setMe(r.body);
      else signOut(r.status === 401 || r.status === 403 ? (r.body.error || '') : '');
      setBooting(false);
    })();
    return () => { live = false; };
  }, [token, signOut]);

  const onSignedIn = useCallback((t) => {
    writeToken(t);
    // Strip `?signin=` so a reload does not try to spend a spent link.
    window.history.replaceState({}, '', window.location.pathname);
    setBooting(true);
    setToken(t);
  }, []);

  const tool = useMemo(() => TOOLS.find((t) => path.startsWith(t.path)) || null, [path]);
  const onExpired = useCallback((msg) => signOut(msg || 'Your session has ended. Sign in again to carry on.'), [signOut]);

  let body;
  if (signinLink && !me) body = <LinkLanding link={signinLink} onSignedIn={onSignedIn} />;
  else if (booting) body = <div className="vt-empty">Loading…</div>;
  else if (!me) body = <SignIn notice={expiredMsg} />;
  else if (!tool) body = <Picker me={me} go={go} />;
  else if (!me.scopes?.includes(tool.id)) {
    body = (
      <div className="vt-empty">
        Your volunteer login doesn’t include the <b>{tool.label}</b>. Ask UMaps if you need it.
        <div style={{ marginTop: 14 }}><button className="vt-btn" onClick={() => go('/volunteer')}>Back to your tools</button></div>
      </div>
    );
  } else if (tool.id === 'moderate') body = <Moderator token={token} onExpired={onExpired} admin={!!me.admin} />;
  else if (tool.id === 'issues') body = <Issues token={token} onExpired={onExpired} admin={!!me.admin} />;
  else if (tool.id === 'rooms') body = <RoomNames token={token} onExpired={onExpired} admin={!!me.admin} />;
  else if (tool.id === 'routes') body = <RouteTester token={token} onExpired={onExpired} admin={!!me.admin} />;
  else body = <ContentFiller token={token} onExpired={onExpired} />;

  // The Route tester is a map: it takes the whole viewport under the header
  // and scrolls its own panel, so the page itself must not (see .vt-bleed).
  const bleed = !!me && tool?.id === 'routes' && !!me.scopes?.includes('routes');

  return (
    <div className={bleed ? 'vt vt-bleed' : 'vt'}>
      <style>{CSS}</style>
      <header className="vt-top">
        <div className="vt-topin">
          <UMapsWordmark tool="Volunteer Tools" size={26} href="/volunteer" />
          {me && (
            <nav className="vt-nav" aria-label="Tools">
              {TOOLS.filter((t) => me.scopes?.includes(t.id)).map((t) => (
                <button key={t.id} className="vt-navbtn" aria-current={tool?.id === t.id ? 'page' : undefined}
                        onClick={() => go(t.path)}>
                  <span aria-hidden="true">{t.icon}</span>{t.label}
                </button>
              ))}
            </nav>
          )}
          {me && (
            <div className="vt-who">
              <span>{me.admin ? 'Signed in as the UMaps operator' : me.email}</span>
              <button className="vt-btn vt-small" onClick={() => signOut('', true)}>Sign out</button>
            </div>
          )}
        </div>
      </header>
      <main className="vt-main">{body}</main>
    </div>
  );
}

/** The one-field form. Says the same thing whatever address was typed. */
function SignIn({ notice }) {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState('');
  const [err, setErr] = useState('');
  const valid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim());

  const submit = async (ev) => {
    ev.preventDefault();
    if (!valid || busy) return;
    setBusy(true); setErr('');
    const r = await api('/api/volunteer-auth', { method: 'POST', body: { op: 'request', email: email.trim() } });
    setBusy(false);
    if (r.ok) setSent(email.trim());
    else setErr(r.body.error || 'That did not go through. Try again in a moment.');
  };

  return (
    <div className="vt-signin">
      <div className="vt-card vt-pad">
        <h1 className="vt-h1">Volunteer sign-in</h1>
        {notice && <div className="vt-note" style={{ marginBottom: 12 }}>{notice}</div>}
        {sent ? (
          <>
            <p className="vt-sub" style={{ marginTop: 10 }}>
              If <b>{sent}</b> has volunteer access, a sign-in link is on its way. It works once and
              expires in 24 hours — check your spam folder if it hasn’t arrived in a couple of minutes.
            </p>
            <button className="vt-btn" onClick={() => { setSent(''); setEmail(''); }}>Use a different email</button>
          </>
        ) : (
          <form onSubmit={submit}>
            <p className="vt-sub">Enter the email UMaps gave access to. We’ll send you a link — there’s no password.</p>
            {err && <div className="vt-err">{err}</div>}
            <div className="vt-field">
              <label className="vt-label" htmlFor="vt-email">Email</label>
              <input id="vt-email" className="vt-input" type="email" autoComplete="email" placeholder="you@umanitoba.ca"
                     value={email} onChange={(e) => setEmail(e.target.value)} autoFocus />
            </div>
            <button className="vt-btn vt-primary" disabled={!valid || busy} style={{ width: '100%', justifyContent: 'center' }}>
              {busy ? 'Sending…' : 'Email me a sign-in link'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

/**
 * Where an emailed link lands. Reads the link (does not spend it), says who it
 * is for, and signs in only when the button is pressed.
 */
function LinkLanding({ link, onSignedIn }) {
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    api('/api/volunteer-auth', { method: 'POST', body: { op: 'check', token: link } }).then((r) => {
      if (!live) return;
      if (r.ok) setInfo(r.body); else setErr(r.body.error || 'That link has expired or has already been used.');
    });
    return () => { live = false; };
  }, [link]);

  const signIn = async () => {
    setBusy(true);
    const r = await api('/api/volunteer-auth', { method: 'POST', body: { op: 'signin', token: link } });
    setBusy(false);
    if (r.ok && r.body.token) onSignedIn(r.body.token);
    else setErr(r.body.error || 'That link has expired or has already been used.');
  };

  return (
    <div className="vt-signin">
      <div className="vt-card vt-pad">
        <h1 className="vt-h1">Volunteer sign-in</h1>
        {err ? (
          <>
            <div className="vt-err" style={{ marginTop: 10 }}>{err}</div>
            <a className="vt-btn" href="/volunteer">Ask for a new link</a>
          </>
        ) : !info ? (
          <p className="vt-sub">Checking your link…</p>
        ) : (
          <>
            <p className="vt-sub" style={{ marginTop: 10 }}>
              This link signs in <b>{info.email}</b>{info.name ? <> ({info.name})</> : null}.
            </p>
            <button className="vt-btn vt-primary" onClick={signIn} disabled={busy} style={{ width: '100%', justifyContent: 'center' }}>
              {busy ? 'Signing in…' : 'Sign in'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function Picker({ me, go }) {
  return (
    <>
      <h1 className="vt-h1">{me.name ? `Hi ${me.name}` : 'Volunteer Tools'}</h1>
      <p className="vt-sub">Pick what you’re working on. Everything you change here reaches the website and the UMaps app.</p>
      <div className="vt-picker">
        {TOOLS.map((t) => {
          const allowed = me.scopes?.includes(t.id);
          return (
            <button key={t.id} className="vt-tool" disabled={!allowed} onClick={() => go(t.path)}>
              <span className="vt-toolicon" aria-hidden="true">{t.icon}</span>
              <span className="vt-toolname">{t.label}</span>
              <span className="vt-toolblurb">{t.blurb}</span>
              {!allowed && <span className="vt-hint">Not included in your login — ask UMaps if you need it.</span>}
            </button>
          );
        })}
      </div>
    </>
  );
}
